import CSS from 'csstype';
import { headerFontFamily } from '@modernhealth/vital-foundation';
import { hexToRgba } from 'src/shared/helpers/hexToRgba';

import { font as _font } from './shared';

const mediaQ = (size: number): string => `@media only screen and (max-width: ${size}px)`;

export const layout = {
    containerPaddingX: 60,
    containerSmallerWidth: 1024,
    containerWidth: 1170,
    containerBiggerWidth: 1440,
    headerHeight: 80,
    modalWidth: {
        wide: 1000,
        medium: 700,
        smallPlus: 570,
        small: 360,
    },
};

export const mediaQueries = {
    xs: mediaQ(380),
    sm: mediaQ(576),
    md: mediaQ(768),
    lg: mediaQ(992),
    xl: mediaQ(1200),
    xxl: mediaQ(1440),
};

interface FontSize {
    smallest: 8;
    xxSmall: 10;
    xSmall: 11;
    small: 12;
    base: 14;
    basePlus: 15;
    medium: 16;
    mediumExtended: 18;
    large: 20;
    largeExtended: 24;
    almostExtraLarge: 26;
    extraLarge: 28;
    header: 30;
    headerPlus: 32;
    headerMedium: 34;
    headerExtended: 40;
    headerBig: 48;
    headerBigger: 52;
    enormous: 60;
    biggest: 70;
    offTheScale: 120;
}

export const font = {
    book: _font(400),
    black: _font(900),
    heavy: _font(700),
    light: _font(300),
    medium: _font(500),
    roman: _font(400),
    size: {
        smallest: 8,
        xxSmall: 10,
        xSmall: 11,
        small: 12,
        base: 14,
        basePlus: 15,
        medium: 16,
        mediumExtended: 18,
        large: 20,
        largeExtended: 24,
        almostExtraLarge: 26,
        extraLarge: 28,
        header: 30,
        headerPlus: 32,
        headerMedium: 34,
        headerExtended: 40,
        headerBig: 48,
        headerBigger: 52,
        enormous: 60,
        biggest: 70,
        offTheScale: 120,
    } as FontSize,
};

// http://chir.ag/projects/name-that-color/#A6B4C5
export const color = {
    weepingWhaleBlue: '#EAF2FC',
    alabaster: '#fcfcfc',
    alabasterDimmed: '#f9f9f9',
    alto: '#d8d8d8',
    aquaSpring: '#eef8fa',
    athensGray: '#f6f6f7',
    azureRadiance: '#007fe9',
    catskillWhite: '#E9EEF4',
    baliHai: '#8197b1',
    blumine: '#1c4b81',
    bayOfMany: '#214d80',
    oxfordBlue: '#354456',
    blueLagoon: '#008491',
    wildWatermelon: '#ff5473',
    bittersweet: '#f87a5b',
    black: '#000000',
    brickRed: '#C52F42',
    nepal: '#92acc8',
    brinkPink: '#f86275',
    capeCod: '#373a39',
    chathamsBlue: '#194e82',
    cadetBlue: '#A6B4C5',
    casper: '#b3c1d2',
    coconut: '#fdfdfd',
    gullGray: '#919fae',
    blueBayoux: '#4d6580',
    bismark: '#426889',
    concrete: '#f3f3f3',
    cornflower: '#89d5e3',
    cornflowerBlue: '#5B82E5',
    steelGray: '#74879c',
    pickledBluewood: '#2e3d4e',
    regentGray: '#7b8da2',
    royalBlue: '#2d80e2',
    bunting: '#2D3142',
    denim: '#1467C9',
    royalBlueSaturated: '#3155ee',
    deepSapphire: '#0C4175',
    doveGray: '#666666',
    zircon: '#f0f5ff',
    dustyGray: '#979797',
    frenchGray: '#bebebf',
    ebonyClay: '#22303F',
    gallery: '#ededed',
    gray05: '#F0F1F4',
    gray10: '#E0E2E9',
    gray85: '#21262E',
    iron: '#cccfd4',
    java: '#1dc6d8',
    jellyBean: '#2774ab',
    lightCyan: '#dcf8f6',
    limedSpruce: '#323C47',
    linkWater: '#dbecf8',
    linkWaterLight: '#e6ecf7',
    malibu: '#76ddfb',
    mamba: '#868287',
    mariner: '#2c82be',
    matisse: '#2176B0',
    mercury: '#e5e5e5',
    mineShaft: '#343434',
    fiord: '#3c4e62',
    monza: '#d0021b',
    nightRider: '#2e2e2e',
    nileStone: '#5eccc4',
    pictonBlue: '#53a8e2',
    polar: '#e9faf9',
    polarDark: '#d7f1f7',
    porcelain: '#e4e8ea',
    raffia: '#E9D8BA',
    riverBed: '#485465',
    robinsEggBlue: '#00bac4',
    rollingStone: '#747a7f',
    saffronMango: '#f9cb4d',
    salmon: '#ff7878',
    scooter: '#36b8d5',
    scorpion: '#5a5a5a',
    selectiveYellow: '#fcbb08',
    shark: '#232425',
    seashell: '#f1f1f1',
    seashellPeach: '#fff6ed',
    silver: '#cac8c8',
    silverChalice: '#B0B0B0',
    spindle: '#bfd3ec',
    sunsetOrange: '#ff4f4f',
    sunShade: '#ffa82c',
    tahitiGold: '#e58b08',
    teal: '#00707b',
    tuna: '#383846',
    tundora: '#4a4a4a',
    verySoftBlue: '#92e9e3',
    violet10: '#E3DFFE',
    vividCyan: '#01C3CD',
    white: '#ffffff',
    paleSlate: '#cecdce',
    bostonBlue: '#3183BC',
    mountainMist: '#9b959c',
    easternBlue: '#1D9FBC',
    pineGreen: '#006B78',
    shakeSpeare: '#60b5cf',
    pattensBlue: '#D8F1F7',
    springGreen: '#00f092',
    slateGray: '#6a7a8e',
    turquoise: '#26d3c6',
    waikawaGray: '#597390',
    texasRose: '#FFA150',
    cornflowerBlueDark: '#607BEF',
    dancingDolphinBlue: '#EFF2FD',
    whiteIce: '#E9F6F8',
    whiteLilac: '#F1F4FA',
    eggSour: '#FFF3DB',
    lightSteelBlue: '#607BEF1A',
    rhino: '#303A68',
    matteJazz: '#0B848C',

    // Semantic color names are below as design system color naming scheme is being figured out
    blockQuoteBackground: '#EEF5FF',
};

export const shadow = {
    lightShadow: `0 2px 13px 0 ${hexToRgba(color.black, 0.03)}`,
    lightCardShadow: `0 0.25rem 1rem ${color.gallery}`,
    mediumShadow: `0 0.25rem 1rem ${color.frenchGray}`,
    heavyShadow: `0 8px 20px 0 ${hexToRgba(color.nightRider, 0.4)}`,
    floatingShadow: `0 0.25rem 0.5rem 0.25rem ${hexToRgba(color.silver, 0.3)}`,
    subtleShadow: `0 0.25rem 1rem ${hexToRgba(color.black, 0.2)}`,
    regularShadow: `0 4px 10px 0 ${hexToRgba(color.iron, 0.5)}`,
};

/**
 * Tokens and styles for design system, based on Foundation Library
 * https://www.figma.com/file/CcMNRwvjCIAU5YBULg4D4b/%F0%9F%A7%A9-Foundation-Library
 */

/**
 * These should be kept in-sync with the changes in Mobile
 * https://gitlab.com/modernhealth/mobile/-/blob/develop/src/common/variables.ts
 */

export interface Typography {
    fontSize?: CSS.FontSizeProperty<string | number>;
    fontFamily?: CSS.FontFamilyProperty;
    fontWeight?: CSS.FontWeightProperty;
    lineHeight?: CSS.LineHeightProperty<string | number>;
    color?: CSS.ColorProperty;
}

enum Heading {
    Heading60 = 'Heading60',
    Heading50 = 'Heading50',
    Heading40 = 'Heading40',
    Heading30 = 'Heading30',
    Heading20 = 'Heading20',
    Heading10 = 'Heading10',
    MarkdownH1 = 'MarkdownH1',
    MarkdownH2 = 'MarkdownH2',
    MarkdownH3 = 'MarkdownH3',
}

function styles<T extends Record<string, CSS.Properties<number | string>>>(
    sheet: T,
): { readonly [P in keyof T]: Readonly<T[P]> } {
    return sheet;
}

const headingBase = {
    fontWeight: 700,
    color: color.ebonyClay,
} as const;

export const headingStyle = styles({
    [Heading.Heading60]: {
        ...headingBase,
        fontSize: 32,
        lineHeight: '40px',
    },
    [Heading.Heading50]: {
        ...headingBase,
        fontSize: 28,
        lineHeight: '36px',
    },
    [Heading.Heading40]: {
        ...headingBase,
        fontSize: 22,
        lineHeight: '28px',
    },
    [Heading.Heading30]: {
        ...headingBase,
        fontSize: 18,
        lineHeight: '24px',
    },
    [Heading.Heading20]: {
        ...headingBase,
        fontSize: 16,
        lineHeight: '20px',
    },
    [Heading.Heading10]: {
        ...headingBase,
        fontSize: 14,
        lineHeight: '20px',
    },
    [Heading.MarkdownH1]: {
        fontFamily: headerFontFamily,
        fontWeight: 900,
        fontSize: 28,
        lineHeight: '36px',
    },
    [Heading.MarkdownH2]: {
        fontFamily: headerFontFamily,
        fontWeight: 900,
        fontSize: 24,
        lineHeight: '32px',
    },
    [Heading.MarkdownH3]: {
        fontFamily: headerFontFamily,
        fontWeight: 700,
        fontSize: 20,
        lineHeight: '28px',
    },
});

export enum Body {
    XLarge = 'XLarge',
    Large = 'Large',
    Medium = 'Medium',
    Small = 'Small',
    XSmall = 'XSmall',
    MarkdownMedium = 'MarkdownMedium',
}

const bodyBase = {
    fontWeight: 400,
    color: color.ebonyClay,
} as const;

export const contentStyle = styles({
    XLarge: {
        ...bodyBase,
        fontSize: 20,
        lineHeight: '32px',
    },
    Large: {
        ...bodyBase,
        fontSize: 18,
        lineHeight: '28px',
    },
    Medium: {
        ...bodyBase,
        fontSize: 16,
        lineHeight: '24px',
    },
    Small: {
        ...bodyBase,
        fontSize: 14,
        lineHeight: '20px',
    },
    XSmall: {
        ...bodyBase,
        fontSize: 12,
        lineHeight: '16px',
    },
    MarkdownMedium: {
        ...bodyBase,
        fontSize: 16,
        lineHeight: '26px',
    },
});
